<template>
    <div>
        <v-date-picker
            ref="calendar"
            mode="single"
            locale="en-GB"
            v-model='date'
            color="primary"
            :formats='formats'
            :highlight="dateAttributes"
            :theme="themeStyles"
            :popover="{ visibility: visibility, align: popoverAlign }"
            :updateOnInput="false"
            @input="onInput"
        >
            <b-input-group slot-scope='{ inputValue, updateValue }'>
                <b-form-input
                    v-model="inputValue"
                    type="text"
                    :id="id"
                    autocomplete="off"
                    :disabled="disabled"
                    :readonly="!typeable"
                    :placeholder="placeholder"
                    @focus.native="setVisiblity('focus')"
                    @input.native='updateValue($event.target.value, { formatInput: false, hidePopover: false
                    })'
                    @change.native='updateValue(updateDate($event.target.value), { formatInput: true, hidePopover:
                    false
                    })'
                >
                </b-form-input>
                <b-input-group-append class="transparent calendar-icon" v-if="calendarIcon">
                    <b-btn>
                        <font-awesome-icon
                                :icon="['far', 'calendar-alt']"
                        >
                        </font-awesome-icon>
                    </b-btn>
                </b-input-group-append>
                <b-input-group-append class="transparent" v-if="clearButton && showConfirmButtons">
                    <b-btn @click.stop.prevent="clearDate" @mousedown.stop.prevent>
                        <font-awesome-icon
                            :icon="['far', 'times']"
                            size="sm"
                        >
                        </font-awesome-icon>
                    </b-btn>
                </b-input-group-append>
                <b-input-group-append class="transparent" v-if="disabled">
                    <b-btn
                        @click="disabled = false"
                    >
                        <font-awesome-icon
                            :icon="['far', 'pencil']"
                            size="xs"
                        >
                        </font-awesome-icon>
                    </b-btn>
                </b-input-group-append>
                <b-input-group-append
                    class="transparent"
                    v-if="showConfirmButtons">
                    <b-btn
                        @click="onCalendarClick"
                        ref="button"
                    >
                        <font-awesome-icon
                            :icon="['far', 'calendar-alt']"
                            size="sm"
                        >
                        </font-awesome-icon>
                    </b-btn>
                    <b-btn
                        @click.stop.prevent="onConfirm"
                        @mousedown.stop.prevent
                    >
                        <font-awesome-icon
                            :icon="['fal', 'check']"
                            class="green"
                            size="sm"
                        >
                        </font-awesome-icon>
                    </b-btn>
                    <b-btn
                        @click="onCancel"
                    >
                        <font-awesome-icon
                            :icon="['far', 'times']"
                            class="red"
                            size="sm"
                        >
                        </font-awesome-icon>
                    </b-btn>
                </b-input-group-append>
                <b-input-group-append
                    class="transparent"
                    v-if="loading">
                    <b-btn
                    >
                        <font-awesome-icon
                            :icon="['fal', 'spinner']"
                            size="sm"
                            spin
                        >
                        </font-awesome-icon>
                    </b-btn>
                </b-input-group-append>
            </b-input-group>
        </v-date-picker>
        <small v-if="helpText && !disabled" class="form-text text-muted mt-2 mb-2">
            {{ helpText }}
        </small>
        <b-button-group
            class="mt-3"
            v-if="reduceButtons && !disabled"
        >
            <b-button
                v-for="week in [1,2,4,8]"
                :key="week"
                variant="outline-primary"
                @click="reduceDateByWeek(week, targetDate)">
                -{{ week }}w
            </b-button>
        </b-button-group>
        <b-button-group
            class="mt-3"
            v-if="extendButtons && !disabled"
        >
            <b-button
                v-for="week in [1,2,4,8]"
                :key="week"
                variant="outline-primary"
                @click="extendDateByWeek(week, targetDate)">
                +{{ week }}w
            </b-button>
        </b-button-group>
    </div>
</template>

<script>
  import dayjs from 'dayjs';

  export default {
  name: 'Date-Picker',
  props: {
    id: {
      type: String,
      default: 'date',
    },
    value: {
      default: null,
    },
    reduceButtons: {
      type: Boolean,
      default: false,
    },
    // Renders +1/2/4/8w shortcuts that push the date forward instead of back.
    // Used by fields that only ever extend a deadline, e.g. the extended due date.
    extendButtons: {
      type: Boolean,
      default: false,
    },
    confirmButtons: {
      type: Boolean,
      default: false,
    },
    calendarIcon: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    targetDate: {},
    mode: {
      type: String,
      default: 'single',
    },
    popoverAlign: {
      default: 'left',
    },
    placeholder: {
      type: String,
      default: '',
    },
    typeable: {
      type: Boolean,
      default: true,
    },
    clearButton: {
      type: Boolean,
      default: false,
    },
    helpText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      disabled: false,
      visibility: 'hidden',
      originalDate: null,
      date: null,
      dateAttributes: {
        highlight: 'rgba(78, 115, 138, 1)',
        borderRadius: '0px',
      },
      formats: {
        weekdays: 'WW',
      },
      themeStyles: {
        header: 'text-primary bg-secondary pb-2',
        headerTitle: '',
        headerArrows: {
          fontSize: '6px',
        },
        weekdays: {
          paddingTop: '7.5px',
          paddingBottom: '7.5px',
        },
        weekdaysHorizontalDivider: 'border-top-1',
      },
    };
  },
  computed: {
    showConfirmButtons() {
      if (this.loading) {
        return false;
      }

      return !this.disabled && this.confirmButtons;
    },
  },
  watch: {
    value: {
      handler(newValue) {
        let date = newValue;
        let originalDate = newValue;

        if (newValue) {
          date = dayjs(newValue)
            .toDate();
          originalDate = dayjs(newValue)
            .toDate();
        }

        this.date = date;
        this.originalDate = originalDate;
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.confirmButtons) {
      this.disabled = true;
    }

    this.setVisiblity();
  },
  methods: {
    reduceDateByWeek(numberOfWeeks, targetDate = new Date()) {
      this.date = dayjs(targetDate)
        .subtract(numberOfWeeks, 'week')
        .toDate();
    },
    extendDateByWeek(numberOfWeeks, targetDate = new Date()) {
      this.date = dayjs(targetDate)
        .add(numberOfWeeks, 'week')
        .toDate();
    },
    setVisiblity(value) {
      if (value) {
        this.visibility = value;
        return;
      }

      if (this.confirmButtons) {
        this.visibility = 'hidden';
        return;
      }

      if (this.disabled) {
        this.visibility = 'hidden';
        return;
      }
      this.visibility = 'focus';
    },
    onInput(value) {
      if (!this.confirmButtons) {
        this.$emit('input', value);
      }
    },
    onConfirm() {
      this.setVisiblity('hidden');
      this.disabled = true;
      this.originalDate = this.date;
      this.$emit('input', this.date);
      this.$emit('confirm', this.date);
    },
    onCancel() {
      this.setVisiblity('hidden');
      this.disabled = true;
      this.date = this.originalDate;
      this.$emit('cancel', true);
    },
    disable() {
      this.disabled = true;
    },
    onCalendarClick() {
      this.$refs.button.focus();
      this.setVisiblity('focus');
    },
    formatDate(value) {
      const dateParts = value.split('/');
      const month = dateParts[1];
      const year = dateParts[2];

      if (dateParts.length <= 1 || !month) {
        return value;
      }

      if (year && year.length === 4) {
        return value;
      }

      let newYear = dayjs().year();
      if (year && year.length === 2) {
        newYear = `20${year}`;
      }

      return `${dateParts[0]}/${month}/${newYear}`;
    },
    updateDate(value) {
      const newDate = this.formatDate(value);

      const { calendar } = this.$refs.calendar.$refs;
      if (calendar) {
        calendar.move(newDate);
      }

      return newDate;
    },
    clearDate() {
      this.date = null;
    },
  },
};
</script>

<style lang="scss">
    .vc-bg-primary-600 {
        background-color: $primary;
    }
    // Unable to turn off border radius
    .vc-rounded-full {
        border-radius: 0px;
    }
</style>
